import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  Search,
  Settings2,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn, formatDateTime } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { getAccountCommunications } from '../api';
import type {
  AccountCommunication,
  AccountCommunicationChannel,
  AccountCommunicationStatusCategory,
  AccountCommunicationsSummary,
} from '../types';

interface CommunicationsTabProps {
  accountId: string;
}

type ChannelFilter = 'all' | AccountCommunicationChannel;
type StatusFilter = 'all' | AccountCommunicationStatusCategory;
type DatePreset = '7d' | '30d' | '90d' | '1y' | 'all';
type FilterType = 'text' | 'select';

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  filterType?: FilterType;
  filterOptions?: { label: string; value: string }[];
  getValue: (event: AccountCommunication) => string | number | null;
  render?: (event: AccountCommunication) => ReactNode;
  align?: 'left' | 'right';
  noFilter?: boolean;
  className?: string;
}

const PAGE_SIZE = 25;
const VISIBILITY_STORAGE_KEY = 'atek-account-communications-column-visibility';
const FILTER_STORAGE_KEY = 'atek-account-communications-column-filters';
const EMPTY_COMMUNICATIONS: AccountCommunication[] = [];

const DATE_PRESETS: Record<DatePreset, { label: string; days?: number }> = {
  '7d': { label: 'Last 7 days', days: 7 },
  '30d': { label: 'Last 30 days', days: 30 },
  '90d': { label: 'Last 90 days', days: 90 },
  '1y': { label: 'Last year', days: 365 },
  all: { label: 'All time' },
};

const CHANNEL_LABELS: Record<ChannelFilter, string> = {
  all: 'All',
  email: 'Email',
  sms: 'SMS',
  call: 'Calls',
  alert_sendout: 'Alerts',
};

const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Succeeded', value: 'success' },
  { label: 'Failed', value: 'failed' },
  { label: 'Pending', value: 'pending' },
  { label: 'Acknowledged', value: 'acknowledged' },
];

function buildColumns(onToggle: (id: string) => void, expandedId: string | null): ColumnDef[] {
  return [
    {
      key: 'occurredAt',
      label: 'Time',
      filterType: 'text',
      getValue: (event) => event.occurredAt,
      render: (event) => <span className="whitespace-nowrap">{formatDateTime(event.occurredAt)}</span>,
    },
    {
      key: 'channel',
      label: 'Channel',
      filterType: 'select',
      filterOptions: [
        { label: 'Email', value: 'email' },
        { label: 'SMS', value: 'sms' },
        { label: 'Call', value: 'call' },
        { label: 'Alert', value: 'alert_sendout' },
      ],
      getValue: (event) => event.channel,
      render: (event) => <ChannelBadge channel={event.channel} />,
    },
    {
      key: 'status',
      label: 'Status',
      filterType: 'select',
      filterOptions: STATUS_OPTIONS.filter((option) => option.value !== 'all'),
      getValue: (event) => event.statusCategory,
      render: (event) => (
        <Badge variant={statusVariant(event.statusCategory)}>
          {event.status || event.statusCategory}
        </Badge>
      ),
    },
    {
      key: 'recipient',
      label: 'Recipient',
      filterType: 'text',
      getValue: (event) => event.recipient,
      render: (event) => (
        <span className="block max-w-[220px] truncate" title={event.recipient}>
          {event.recipient || 'N/A'}
        </span>
      ),
    },
    {
      key: 'subject',
      label: 'Subject / Alert',
      filterType: 'text',
      getValue: (event) => [event.subject, event.bodyPreview].filter(Boolean).join(' '),
      render: (event) => (
        <div className="max-w-[360px]">
          <p className="truncate font-medium" title={event.subject || event.bodyPreview}>
            {event.subject || event.bodyPreview || 'N/A'}
          </p>
          {event.bodyPreview && event.bodyPreview !== event.subject && (
            <p className="truncate text-xs text-muted-foreground" title={event.bodyPreview}>
              {event.bodyPreview}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'notificationType',
      label: 'Purpose',
      filterType: 'text',
      defaultVisible: false,
      getValue: (event) => event.notificationType,
      render: (event) => event.notificationType || 'N/A',
    },
    {
      key: 'providerId',
      label: 'Provider ID',
      filterType: 'text',
      defaultVisible: false,
      getValue: (event) => event.providerId,
      render: (event) => (
        <span className="font-mono text-xs" title={event.providerId}>
          {event.providerId || 'N/A'}
        </span>
      ),
    },
    {
      key: 'error',
      label: 'Issue',
      filterType: 'text',
      getValue: (event) => event.deliveryError,
      render: (event) => event.deliveryError ? (
        <span className="text-danger" title={event.deliveryError}>
          {event.deliveryError}
        </span>
      ) : 'N/A',
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      align: 'right',
      getValue: () => '',
      render: (event) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => onToggle(event.id)}
          aria-label={expandedId === event.id ? 'Collapse details' : 'Expand details'}
        >
          {expandedId === event.id ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      ),
    },
  ];
}

const STATIC_COLUMNS = buildColumns(() => undefined, null);

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const column of STATIC_COLUMNS) {
    defaults[column.key] = column.defaultVisible !== false;
  }
  return defaults;
}

function readLocalStorage(key: string): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage.getItem(key);
}

function writeLocalStorage(key: string, value: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(key, value);
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = readLocalStorage(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    // Ignore invalid persisted UI state.
  }
  return getDefaultVisibility();
}

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(readLocalStorage(FILTER_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function resolveDateRange(preset: DatePreset): { from?: string; to?: string } {
  const option = DATE_PRESETS[preset];
  if (!option.days) return {};
  const to = new Date();
  const from = new Date(to.getTime() - option.days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function CommunicationsTab({ accountId }: CommunicationsTabProps) {
  const [page, setPage] = useState(0);
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [datePreset, setDatePreset] = useState<DatePreset>('90d');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);

  useEffect(() => {
    writeLocalStorage(VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    writeLocalStorage(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    setPage(0);
    setExpandedId(null);
  }, [channel, status, datePreset, debouncedSearch]);

  const dateRange = useMemo(() => resolveDateRange(datePreset), [datePreset]);

  const query = useQuery({
    queryKey: ['account-communications', accountId, page, channel, status, datePreset, debouncedSearch, dateRange.from, dateRange.to],
    queryFn: () => getAccountCommunications(accountId, {
      pageNumber: page,
      pageLength: PAGE_SIZE,
      channel,
      status,
      search: debouncedSearch || undefined,
      from: dateRange.from,
      to: dateRange.to,
    }),
    enabled: !!accountId,
  });

  const communications = query.data?.data ?? EMPTY_COMMUNICATIONS;
  const summary = query.data?.summary || emptySummary();
  const total = query.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo(
    () => buildColumns(
      (id) => setExpandedId((current) => (current === id ? null : id)),
      expandedId,
    ),
    [expandedId],
  );

  const visibleColumns = columns.filter((column) => columnVisibility[column.key] !== false);

  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, value]) => value);
    if (!activeFilters.length) return communications;

    return communications.filter((event) =>
      activeFilters.every(([columnKey, filterValue]) => {
        const column = columns.find((item) => item.key === columnKey);
        if (!column || column.noFilter) return true;

        const rawValue = column.getValue(event);
        if (column.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        return String(rawValue || '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [communications, columnFilters, columns]);

  const toggleColumn = (key: string) => {
    setColumnVisibility((current) => ({
      ...current,
      [key]: current[key] === false,
    }));
  };

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
  };

  const clearColumnFilters = () => setColumnFilters({});

  if (query.error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading communications</p>
          <p className="text-sm text-muted-foreground">
            {query.error instanceof Error ? query.error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => query.refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Communications</CardTitle>
            <CardDescription>
              {filteredItems.length < communications.length
                ? `Showing ${filteredItems.length} of ${communications.length} on this page`
                : `Showing ${communications.length} of ${total}`}
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(filteredItems, columns)}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportToCSV(filteredItems, columns, 'account-communications')}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.filter((column) => !column.noFilter).map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={columnVisibility[column.key] !== false}
                    onCheckedChange={() => toggleColumn(column.key)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <SummaryStrip summary={summary} />

        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup
            type="single"
            value={channel}
            onValueChange={(value) => value && setChannel(value as ChannelFilter)}
            className="justify-start rounded-md border bg-background p-1"
          >
            {(Object.keys(CHANNEL_LABELS) as ChannelFilter[]).map((value) => (
              <ToggleGroupItem
                key={value}
                value={value}
                size="sm"
                className="h-7 px-2 text-xs data-[state=on]:bg-atek-blue data-[state=on]:text-white"
              >
                {CHANNEL_LABELS[value]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={datePreset} onValueChange={(value) => setDatePreset(value as DatePreset)}>
            <SelectTrigger className="h-9 w-[140px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(DATE_PRESETS) as Array<[DatePreset, { label: string }]>).map(([value, option]) => (
                <SelectItem key={value} value={value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="h-9 pl-8"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search subject, recipient, provider ID, or error"
            />
          </div>

          {Object.values(columnFilters).some(Boolean) && (
            <Button variant="ghost" size="sm" onClick={clearColumnFilters}>
              Clear column filters
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((column) =>
                  renderColumnFilter(column, columnFilters, setColumnFilter),
                )}
              </TableRow>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableHead
                    key={column.key}
                    className={cn(column.align === 'right' && 'text-right')}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading && Array.from({ length: 8 }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {!query.isLoading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No communications found
                  </TableCell>
                </TableRow>
              )}

              {!query.isLoading && filteredItems.map((event) => (
                <Fragment key={event.id}>
                  <TableRow key={event.id} className="hover:bg-muted/50">
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          'text-sm',
                          column.align === 'right' && 'text-right',
                          column.className,
                        )}
                      >
                        {column.render ? column.render(event) : String(column.getValue(event) || '') || 'N/A'}
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandedId === event.id && (
                    <TableRow key={`${event.id}-detail`}>
                      <TableCell colSpan={visibleColumns.length} className="bg-muted/20 p-0">
                        <CommunicationDetailPanel event={event} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryStrip({ summary }: { summary: AccountCommunicationsSummary }) {
  const items = [
    { label: 'Total', value: summary.total.toLocaleString(), tone: 'default' },
    { label: 'Failures', value: summary.failed.toLocaleString(), tone: summary.failed ? 'danger' : 'muted' },
    {
      label: 'Unacked Alerts',
      value: summary.unacknowledgedAlerts.toLocaleString(),
      tone: summary.unacknowledgedAlerts ? 'warning' : 'muted',
    },
    {
      label: 'Last Communication',
      value: summary.lastCommunicationAt ? formatDateTime(summary.lastCommunicationAt) : 'N/A',
      tone: 'default',
    },
  ];

  return (
    <div className="grid gap-2 md:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.label}
          className={cn(
            'rounded-md border px-3 py-2',
            item.tone === 'danger' && 'border-danger/30 bg-red-50',
            item.tone === 'warning' && 'border-warning/40 bg-yellow-50',
            item.tone === 'muted' && 'bg-muted/30',
          )}
        >
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {item.label}
          </p>
          <p className="mt-1 text-sm font-semibold text-foreground">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

function renderColumnFilter(
  column: ColumnDef,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (column.noFilter) return <TableHead key={`filter-${column.key}`} />;

  if (column.filterType === 'select' && column.filterOptions) {
    const selected = parseSelectedFilter(columnFilters[column.key]);
    const toggle = (value: string) => {
      const next = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      setColumnFilter(column.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${column.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full justify-start text-xs font-normal">
              {selected.length ? `${selected.length} selected` : 'Filter'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {column.filterOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selected.includes(option.value)}
                onCheckedChange={() => toggle(option.value)}
                onSelect={(event) => event.preventDefault()}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${column.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[column.key] || ''}
        onChange={(event) => setColumnFilter(column.key, event.target.value)}
        placeholder="Filter"
      />
    </TableHead>
  );
}

function parseSelectedFilter(value?: string): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function ChannelBadge({ channel }: { channel: AccountCommunicationChannel }) {
  const iconMap = {
    email: Mail,
    sms: MessageSquare,
    call: Phone,
    alert_sendout: ShieldAlert,
  };
  const Icon = iconMap[channel];
  return (
    <Badge variant="outline" className="gap-1 whitespace-nowrap normal-case">
      <Icon className="h-3 w-3" />
      {CHANNEL_LABELS[channel]}
    </Badge>
  );
}

function statusVariant(status: AccountCommunicationStatusCategory) {
  if (status === 'failed') return 'danger';
  if (status === 'pending') return 'warning';
  if (status === 'acknowledged') return 'info';
  return 'success';
}

function CommunicationDetailPanel({ event }: { event: AccountCommunication }) {
  const detailEntries = Object.entries(event.detail || {})
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '');

  return (
    <div className="space-y-4 px-6 py-4">
      <div className="grid gap-3 md:grid-cols-4">
        <DetailValue label="Source ID" value={event.sourceId} mono />
        <DetailValue label="Provider ID" value={event.providerId} mono />
        <DetailValue label="Alert Sendout" value={event.alertSendoutId} mono />
        <DetailValue label="Alert History" value={event.alertHistoryId} mono />
      </div>

      {event.deliveryError && (
        <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-red-50 px-3 py-2 text-sm text-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{event.deliveryError}</span>
        </div>
      )}

      {detailEntries.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Event Evidence
          </p>
          <div className="grid gap-2 md:grid-cols-3">
            {detailEntries.map(([key, value]) => (
              <DetailValue key={key} label={formatDetailLabel(key)} value={String(value)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailValue({ label, value, mono = false }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded-md border bg-background px-3 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'mt-1 truncate text-sm',
          mono && 'font-mono text-xs',
          !value && 'text-muted-foreground',
        )}
        title={value || undefined}
      >
        {value || 'N/A'}
      </p>
    </div>
  );
}

function formatDetailLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (first) => first.toUpperCase());
}

function emptySummary(): AccountCommunicationsSummary {
  return {
    total: 0,
    failed: 0,
    unacknowledgedAlerts: 0,
    lastCommunicationAt: null,
    channels: {
      email: 0,
      sms: 0,
      call: 0,
      alertSendout: 0,
    },
  };
}

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    text = `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function exportToCSV(items: AccountCommunication[], columns: ColumnDef[], filename: string) {
  const exportColumns = columns.filter((column) => !column.noFilter);
  const header = exportColumns.map((column) => escapeCSVValue(column.label)).join(',');
  const rows = items.map((item) =>
    exportColumns.map((column) => escapeCSVValue(column.getValue(item))).join(','),
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${items.length} rows`);
}

async function copyToClipboard(items: AccountCommunication[], columns: ColumnDef[]) {
  const exportColumns = columns.filter((column) => !column.noFilter);
  const header = exportColumns.map((column) => column.label).join('\t');
  const rows = items.map((item) =>
    exportColumns.map((column) => String(column.getValue(item) || '')).join('\t'),
  );
  await navigator.clipboard.writeText([header, ...rows].join('\n'));
  toast.success('Copied to clipboard');
}
